import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { userService, projectService } from '../services/api';

const ManageUsersModal = ({ workspace, onClose }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, [workspace]);

  const fetchData = async () => {
    try {
      let usersData = [];
      const currentUserRole = currentUser?.role?.toLowerCase();
      
      
      if (currentUserRole === 'administrator') {
        // Administrators can add anyone from any organization
        usersData = await userService.getUsers();
      } else if (currentUserRole === 'instructor') {
        // Instructors can only add analysts from the same organization
        if (!currentUser.organization) {
          usersData = [];
        } else {
          const params = {
            organization: currentUser.organization
          };
          
          // Let's also try without organization filter to see all users
          const allUsers = await userService.getUsers();
          
          const allOrgUsers = await userService.getUsers(params);
          usersData = allOrgUsers.filter(user => {
            const isAnalyst = user.role?.toLowerCase() === 'analyst';
            return isAnalyst;
          });
        }
      } else {
        // Analysts cannot add analysts at all
        usersData = [];
      }
      
      setAllUsers(usersData);

      // Get current workspace participants
      const currentParticipants = workspace.participants || [];
      setWorkspaceUsers(currentParticipants.map(p => p.id));
    } catch (error) {
      toast.error(t('manageUsers.fetchError'));
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (userId) => {
    try {
      await projectService.addParticipant(workspace.id, userId);
      setWorkspaceUsers([...workspaceUsers, userId]);
    } catch (error) {
      toast.error(t('manageUsers.addError'));
      console.error('Error adding user:', error);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await projectService.removeParticipant(workspace.id, userId);
      setWorkspaceUsers(workspaceUsers.filter(id => id !== userId));
    } catch (error) {
      toast.error(t('manageUsers.removeError'));
      console.error('Error removing user:', error);
    }
  };

  const filteredUsers = allUsers.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const isUserInWorkspace = (userId) => workspaceUsers.includes(userId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-neutral-900">
              {t('manageUsers.title')}
            </h2>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-500 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-neutral-600 mt-1">
            {t('manageUsers.subtitle', { name: workspace.name })}
          </p>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-neutral-200">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('manageUsers.searchPlaceholder')}
              className="w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 200px)' }}>
          {loading ? (
            <div className="p-6 text-center text-neutral-500">
              {t('common.loading')}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-6 text-center text-neutral-500">
              {t('manageUsers.noUsersFound')}
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {filteredUsers.map(user => {
                const isInWorkspace = isUserInWorkspace(user.id);
                return (
                  <div key={user.id} className="px-6 py-4 hover:bg-neutral-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
                          <span className="text-accent-700 font-medium text-sm">
                            {user.full_name ? user.full_name.split(' ').map(n => n[0]).slice(0, 2).join('') : user.username[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-neutral-900">
                              {user.full_name || user.username}
                            </h4>
                            <span className="text-xs text-neutral-500">
                              @{user.username}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-500">{user.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {isInWorkspace ? (
                          <>
                            <span className="text-xs text-success-dark bg-success-light px-2 py-1 rounded">
                              {t('manageUsers.inWorkspace')}
                            </span>
                            <button
                              onClick={() => handleRemoveUser(user.id)}
                              className="px-3 py-1 text-sm font-medium text-error-600 border border-error-300 rounded-md hover:bg-error-50 transition-colors"
                            >
                              {t('common.delete')}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleAddUser(user.id)}
                            className="px-3 py-1 text-sm font-medium text-accent-600 border border-accent-300 rounded-md hover:bg-accent-50 transition-colors"
                          >
                            {t('manageUsers.addToWorkspace')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-neutral-600">
              {t('manageUsers.analystsAssigned', { count: workspaceUsers.length })}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageUsersModal;